import React, { useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import classNames from 'classnames';
import { useAuth } from '@/hooks/useAuth';
import { setMode } from '@/store/slices/theme/themeSlice';
import { FiSun, FiMoon } from 'react-icons/fi';

interface HeaderProps {
  toggleSidebar: () => void;
}

// Memoize the Header component to prevent unnecessary re-renders
const Header: React.FC<HeaderProps> = memo(({ toggleSidebar }) => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state: RootState) => state.theme);
  const { user } = useSelector((state: RootState) => state.auth);
  const { logout } = useAuth();

  // Use useCallback to memoize the function
  const handleSignOut = useCallback(() => {
    logout();
  }, [logout]);

  const toggleTheme = useCallback(() => {
    dispatch(setMode(mode === 'light' ? 'dark' : 'light'));
  }, [dispatch, mode]);

  return (
    <header className={classNames('sticky top-0 z-30 border-b', {
      'bg-white border-gray-200': mode === 'light',
      'bg-gray-800 border-gray-700': mode === 'dark'
    })}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 -mb-px">
          {/* Left: Hamburger button */}
          <button
            className="text-gray-500 hover:text-gray-600 lg:hidden"
            onClick={toggleSidebar}
            aria-controls="sidebar"
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="5" width="16" height="2" />
              <rect x="4" y="11" width="16" height="2" />
              <rect x="4" y="17" width="16" height="2" />
            </svg>
          </button>

          {/* Logo */}
          <div className="flex items-center">
            <h1 className="text-xl font-semibold"></h1>
          </div>

          {/* Right: User menu */}
          <div className="flex items-center space-x-3">
            {/* Theme toggle */}
            <button
              className={classNames(
                'inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors',
                {
                  'bg-gray-100 text-gray-700 hover:bg-gray-200': mode === 'light',
                  'bg-gray-700 text-gray-200 hover:bg-gray-600': mode === 'dark',
                }
              )}
              onClick={toggleTheme}
              aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {mode === 'light' ? <FiMoon /> : <FiSun />}
            </button>
            {/* User menu */}
            <div className="relative inline-flex">
              <button
                className={classNames("inline-flex justify-center items-center px-3 py-1.5 rounded-md", {
                  "bg-red-500 hover:bg-red-600 text-white": true
                })}
                onClick={handleSignOut}
              >
                <svg 
                  className="w-4 h-4 mr-1.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                  />
                </svg>
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
});

export default Header;